import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Zap, Target, CheckCircle2, Star, Users, Gift, Sparkles } from 'lucide-react';
import { useMotionValue, useTransform, animate, useInView, AnimatePresence } from 'framer-motion';
import { UrgencyBanner } from './components/UrgencyBanner';
import { BonusCard } from './components/BonusCard';
import { Reviews } from './components/Reviews';
import { FAQ } from './components/FAQ';
import { WhatYouReceive } from './components/WhatYouReceive';
import { PortalTerapeutico } from './components/PortalTerapeutico';
import { LavenderBranches } from './components/LavenderBranches';
import { HeroAmbientEffects } from './components/HeroAmbientEffects';
import { GiftBackground } from './components/GiftBackground';
import { LifetimeAccessBonus } from './components/LifetimeAccessBonus';
import { PremiumCheckout } from './components/PremiumCheckout';
import { CTAButton } from './components/CTAButton';
function AnimatedCounter({
  from,
  to
}: {
  from: number;
  to: number;
}) {
  const count = useMotionValue(from);
  const rounded = useTransform(count, latest => {
    return Math.round(latest).toLocaleString('es-AR');
  });
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (isInView) {
      const controls = animate(count, to, {
        duration: 1.5,
        ease: 'easeOut'
      });
      return controls.stop;
    }
  }, [count, to, isInView]);
  return <motion.span ref={ref} data-id="element-0">{rounded}</motion.span>;
}
const testimonials = [{
  image: 'https://cdn.magicpatterns.com/uploads/1y1bQtuJgFGCAvQdNRgBRr/ChatGPT_Image_19_may_2026,_14_11_19.webp'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/kGsfNr5RRLf5VRi4YfeMZN/ChatGPT_Image_19_may_2026,_14_12_24.webp'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/vMBqhduTkAY88ALqYDheNZ/ChatGPT_Image_19_may_2026,_14_13_56.webp'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/jiyPjkZE44H9g6ZRyV5RKt/ChatGPT_Image_19_may_2026,_14_15_35.webp'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/8mT9D7xuYz4azHJCLLSXU2/ChatGPT_Image_19_may_2026,_14_17_01.webp'
}];
const heroImages = ['https://cdn.magicpatterns.com/uploads/7hAbtPEv66tS7cRaYNE4r9/me_gusta_mucho_202604131502.png', 'https://cdn.magicpatterns.com/uploads/pYiKeNHymWKoebcvZXR1Ds/Diseno_sin_titulo_(1).png'];
export function App() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [spots, setSpots] = useState(18);
  const [joinedCount, setJoinedCount] = useState(3265);
  const [viewers, setViewers] = useState(48);
  const [showSaleAnimations, setShowSaleAnimations] = useState(false);
  const [timeLeft, setTimeLeft] = useState(900); // 15 minutes in seconds
  const [selectedTier, setSelectedTier] = useState<'solo' | 'bundle' | 'full'>('solo');
  const [selectedOptional, setSelectedOptional] = useState<'radiestesia' | 'constelaciones' | 'tarot'>('radiestesia');
  useEffect(() => {
    const viewerInterval = setInterval(() => {
      setViewers(prev => prev + (Math.random() > 0.5 ? 1 : -1));
    }, 3000);
    return () => clearInterval(viewerInterval);
  }, []);
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  useEffect(() => {
    // Meta Pixel Integration
    const pixelId = '922140294216102';
    // @ts-ignore
    if (!window.fbq) {
      // @ts-ignore
      !function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        b.head.appendChild(t);
      }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    }
    // @ts-ignore
    window.fbq('init', pixelId);
    // @ts-ignore
    window.fbq('track', 'PageView');
  }, []);
  const handleSale = () => {
    setSpots(prev => prev > 2 ? prev - 1 : prev);
    setJoinedCount(prev => prev + 1);
    setShowSaleAnimations(true);
    setTimeout(() => setShowSaleAnimations(false), 2000);
  };
  const nextTestimonial = () => {
    setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
  };
  const prevTestimonial = () => {
    setCurrentTestimonial(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  const currentDate = new Date().toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const scrollToPurchase = () => {
    document.getElementById('purchase')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const tiers = [{
    id: 'solo',
    name: 'Solo Kit Profesional Reiki',
    originalPrice: '$59.990',
    price: '$19.990',
    description: 'Guía Reiki Terapéutico PDF + 5 Bonos',
    checkoutUrl: 'https://www.paranosotras.shop/cart/51917365936260:1'
  }, {
    id: 'bundle',
    name: 'Guía Reiki + 1 Kit Opcional',
    originalPrice: '$79.990',
    price: '$29.990',
    description: 'Elegí un kit adicional para complementar tu formation',
    checkoutUrl: 'https://www.paranosotras.shop/cart/51917365936260:1',
    badge: 'MÁS ELEGIDO'
  }, {
    id: 'full',
    name: 'PORTAL TERAPÉUTICO COMPLETO ✨',
    originalPrice: '$99.990',
    price: '$39.990',
    description: 'Acceso total: Reiki + Radiestesia + Constelaciones + Tarot + Todos sus bonos',
    checkoutUrl: 'https://www.paranosotras.shop/cart/51917365936260:1',
    badge: 'MEJOR VALOR 🔥'
  }];
  const optionalKits = [{
    id: 'radiestesia',
    name: 'Kit Radiestesia Profesional',
    icon: '🌿'
  }, {
    id: 'constelaciones',
    name: 'Kit Constelaciones Familiar',
    icon: '🪷'
  }, {
    id: 'tarot',
    name: 'Kit Tarot Terapéutico',
    icon: '🔮'
  }];
  return <div className="min-h-screen bg-[#FEFEFE] text-[#2D2D2D] font-sans selection:bg-[#FCE4EC] selection:text-[#C2185B]" data-id="element-1">
      <LavenderBranches data-id="element-2" />
      <div className="fixed bottom-0 right-0 pointer-events-none z-50 rotate-180 opacity-60" data-id="element-3">
        <LavenderBranches data-id="element-4" />
      </div>
      <div className="fixed top-1/2 -left-20 pointer-events-none z-50 -rotate-90 opacity-40" data-id="element-5">
        <LavenderBranches data-id="element-6" />
      </div>
      <UrgencyBanner spots={spots} onSale={handleSale} viewers={viewers} data-id="element-7" />

      <div className="relative" data-id="element-8">
        {/* Hero Section */}
        <header className="relative overflow-hidden bg-[#0f051a]" data-id="element-9">
          <HeroAmbientEffects data-id="element-10" />
          <div className="relative" data-id="element-11">
            <img src="https://cdn.magicpatterns.com/uploads/dF9EwnXJjZpavVNq8Uou4H/b0d5db19-037a-41be-b21f-524bb9aee271.webp" alt="Mi Psicóloga Me Dijo - 130 razones para elegirte sin culpa" className="w-full h-auto block" data-id="element-12" />

            {/* Premium CTA Button Overlay */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 z-20 w-full max-w-4xl px-4 text-center" data-id="element-13">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5
            }} className="flex flex-col items-center relative" data-id="element-14">
                {/* Premium Breathing Glow Behind CTA */}
                <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[120%] bg-gradient-to-r from-[#D4AF37]/20 via-[#A78BFA]/20 to-[#D4AF37]/20 blur-2xl rounded-full pointer-events-none" animate={{
                opacity: [0.3, 0.6, 0.3],
                scale: [0.9, 1.1, 0.9]
              }} transition={{
                duration: 6,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-15" />

                <p className="text-[10px] md:text-xs font-bold tracking-wide mt-2 text-[#DD8288]" data-id="element-16">
                  Acceso inmediato • Descarga digital • Garantía 7 días
                </p>
              </motion.div>
            </div>
          </div>
          <img src="https://cdn.magicpatterns.com/uploads/qSH5HSsDBjZojky5dxcdsY/53b616b8-7e20-4dab-8a73-bb9568b8e6e7.webp" alt="Este libro es para vos si..." className="w-full h-auto block" data-id="element-17" />
          <img src="https://cdn.magicpatterns.com/uploads/8ZxZBG7DfgLDxvg8bdcyxR/d9b4ebff-409f-48a2-9c46-11fc9709c5dc.webp" alt="Si elegís priorizarte..." className="w-full h-auto block" data-id="element-18" />
          <img src="https://cdn.magicpatterns.com/uploads/ufFTVvzXwMFbXCR3VjXTcC/959a04d0-a6cb-45dc-848d-0c0982769c44.webp" alt="Lo que dicen quienes ya lo leyeron" className="w-full h-auto block" data-id="element-19" />
        </header>

        <div className="py-12 px-4 bg-white pt-[0px] pb-[0px]" data-id="element-20">
          <CTAButton data-id="element-21" />
        </div>

        <WhatYouReceive data-id="element-22" />

        <PortalTerapeutico data-id="element-23" />
      </div>

      {/* Purchase Section */}
      <PremiumCheckout spots={spots} joinedCount={joinedCount} data-id="element-24" />

      {/* Creator Section */}
      <section className="bg-white" data-id="element-25">
        <img src="https://cdn.magicpatterns.com/uploads/esFD4rJ6Ux9uc4uUYpdxKq/88eff15a-ba5b-476b-a1f3-97789b171a8d.webp" alt="Recibirás 6 libros de regalo totalmente gratis" className="w-full h-auto block" data-id="element-26" />
      </section>

      <section className="bg-white" data-id="element-27">
        <img src="https://cdn.magicpatterns.com/uploads/jj17d2ufm9vfnvJqt7Ki3w/23f7cc50-2d92-4347-b78b-03da53e1d100.webp" alt="Información importante sobre tu compra segura" className="w-full h-auto block" data-id="element-28" />
      </section>

      <Reviews data-id="element-29" />

      <div className="py-12 px-4 pt-[0px] pb-[0px] bg-white" data-id="element-30">
        <CTAButton data-id="element-31" />
      </div>

      <FAQ data-id="element-32" />

      <footer className="py-12 border-t border-[#FDF2F2] text-center text-[#999999] text-sm bg-white" data-id="element-33">
        <p data-id="element-34">© 2025 PARA NOSOTRAS. Todos los derechos reservados.</p>
      </footer>
    </div>;
}